import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  QUIZ_OPTIONS_QUERY,
  SAVE_ALTKLAUSUR_QUIZ_SESSION_ANSWER_MUTATION,
  START_ALTKLAUSUR_QUIZ_SESSION_MUTATION,
} from "./studentSubjectGraphql";
import { normalizeRichHtmlForDisplay, richHtmlToPlainText } from "../../../utils/richContent";

// Shared student practice UI for lesson and Altklausur Aufgaben/Quizfragen.

export const getPositionValue = (item) => {
  const parsed = Number(item?.position);
  return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
};

export const sortByPositionThenId = (a, b) => {
  const diff = getPositionValue(a) - getPositionValue(b);
  if (diff !== 0) return diff;
  return String(a?.id ?? "").localeCompare(String(b?.id ?? ""));
};

export const isMultipleQuizType = (value) =>
  value === "multiple_choice" || value === "multi_select";

export const isRequiredQuiz = (quiz) => quiz?.requiredForCompletion !== false;

const isCorrectOption = (option) =>
  option?.isCorrect === true || option?.is_correct === true;

const taskField = (task, camelName, snakeName, fallback = "") =>
  task?.[camelName] ?? task?.[snakeName] ?? fallback;

const taskId = (task) => String(task?.id || "");

const isGeneratedLessonTask = (task) => {
  const sourceType = String(taskField(task, "sourceType", "source_type", "")).toLowerCase();
  return (
    sourceType === "ai" ||
    Boolean(taskField(task, "generatedForUserID", "generated_for_user_id", ""))
  );
};

const getOriginTaskId = (task) =>
  String(taskField(task, "originTaskID", "origin_task_id", "") || "");

const buildLessonTaskGroups = (tasks) => {
  const safeTasks = tasks || [];
  const manualTasks = safeTasks.filter((task) => !isGeneratedLessonTask(task));
  const manualIds = new Set(manualTasks.map(taskId).filter(Boolean));
  const generatedByOrigin = new Map();

  safeTasks
    .filter(isGeneratedLessonTask)
    .forEach((task) => {
      const originId = getOriginTaskId(task);
      if (originId && manualIds.has(originId)) {
        const current = generatedByOrigin.get(originId) || [];
        current.push(task);
        generatedByOrigin.set(originId, current);
      }
    });

  return manualTasks.map((task) => ({
    task,
    generatedTasks: (generatedByOrigin.get(taskId(task)) || []).slice().sort(sortByPositionThenId),
  }));
};

export function PracticeExpandButton({ onClick, label = "Großansicht öffnen" }) {
  return (
    <button
      type="button"
      className="practice-expand-btn"
      onClick={onClick}
      aria-label={label}
      data-tooltip="Großansicht"
    >
      <span className="practice-expand-icon" aria-hidden="true">
        <span className="practice-expand-arrow practice-expand-arrow-ne">↗</span>
        <span className="practice-expand-arrow practice-expand-arrow-sw">↙</span>
      </span>
    </button>
  );
}

export function PracticeFullscreenModal({ open, title, subtitle, onClose, children }) {
  useEffect(() => {
    if (!open) return undefined;

    const root = document.documentElement;
    const body = document.body;
    const previousHtmlOverflow = root.style.overflow;
    const previousHtmlOverscroll = root.style.overscrollBehavior;
    const previousBodyOverflow = body.style.overflow;
    const previousBodyOverscroll = body.style.overscrollBehavior;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    root.classList.add("practice-modal-open");
    body.classList.add("practice-modal-open");
    root.style.overflow = "hidden";
    root.style.overscrollBehavior = "none";
    body.style.overflow = "hidden";
    body.style.overscrollBehavior = "none";
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      root.style.overflow = previousHtmlOverflow;
      root.style.overscrollBehavior = previousHtmlOverscroll;
      body.style.overflow = previousBodyOverflow;
      body.style.overscrollBehavior = previousBodyOverscroll;
      root.classList.remove("practice-modal-open");
      body.classList.remove("practice-modal-open");
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="practice-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose?.();
        }
      }}
    >
      <section
        className="practice-modal-shell"
        role="dialog"
        aria-modal="true"
        aria-label={title || "Großansicht"}
        onWheel={(event) => event.stopPropagation()}
        onTouchMove={(event) => event.stopPropagation()}
      >
        <header className="practice-modal-head">
          <div>
            {subtitle ? <span>{subtitle}</span> : null}
            <h2>{title || "Großansicht"}</h2>
          </div>
          <button
            type="button"
            className="practice-modal-close"
            onClick={onClose}
            aria-label="Großansicht schließen"
          >
            ×
          </button>
        </header>
        <div className="practice-modal-content">{children}</div>
      </section>
    </div>
  );
}

export function QuizBlock({
  quiz,
  selected,
  onSelect,
  onOptionsLoaded,
  evaluate,
  wasCorrect,
  index,
}) {
  const { data: optionsData } = useQuery(QUIZ_OPTIONS_QUERY, {
    variables: { quizId: quiz.id },
  });

  const options = useMemo(
    () => (optionsData?.quizOptionsByQuiz || []).slice().sort(sortByPositionThenId),
    [optionsData]
  );
  const selectedIds = Array.isArray(selected) ? selected : [];
  const isMultiple = isMultipleQuizType(quiz.questionType);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (options.length > 0) {
      onOptionsLoaded?.(quiz.id, options);
    }
  }, [onOptionsLoaded, options, quiz.id]);

  const renderQuizBlock = (isExpandedView = false) => (
    <div
      className={`quiz-block practice-quiz-block ${isExpandedView ? "practice-expanded-card" : ""} ${
        wasCorrect
          ? "correct-block"
          : evaluate && !wasCorrect
          ? "incorrect-block"
          : ""
      }`}
    >
      <div className="practice-quiz-header">
        <div className="practice-quiz-titlewrap">
          <span className="practice-quiz-kicker">Quizfrage {index + 1}</span>
          <div
            className="practice-quiz-question lh-lessonContent"
            dangerouslySetInnerHTML={{
              __html: normalizeRichHtmlForDisplay(quiz.question, "Quizfrage"),
            }}
          />
        </div>
        <div className="practice-card-actions">
          <div className="practice-quiz-badges">
            <span className="practice-quiz-mode">
              {isMultiple ? "Mehrfachauswahl" : "Single Choice"}
            </span>
            {quiz.difficulty ? (
              <span className="practice-quiz-difficulty">{quiz.difficulty}</span>
            ) : null}
            {isRequiredQuiz(quiz) ? <span className="practice-quiz-required">Pflicht</span> : null}
            {quiz.isKlausurRelevant ? (
              <span className="practice-quiz-exam">Klausurrelevant</span>
            ) : null}
          </div>
          {!isExpandedView ? (
            <PracticeExpandButton
              onClick={() => setIsExpanded(true)}
              label={`Quizfrage ${index + 1} in Großansicht öffnen`}
            />
          ) : null}
        </div>
      </div>

      <div className="practice-quiz-options">
        {options.map((opt) => {
          const isSel = selectedIds.includes(opt.id);
          let stateClass = "";

          if (wasCorrect) {
            if (isCorrectOption(opt)) stateClass = "correct";
          } else if (evaluate) {
            if (isSel) stateClass = isCorrectOption(opt) ? "correct" : "incorrect";
          } else if (isSel) {
            stateClass = "selected";
          }

          const marker =
            stateClass === "correct" ? "✓" : stateClass === "incorrect" ? "×" : "";

          return (
            <button
              type="button"
              key={opt.id}
              className={`quiz-option ${stateClass}`}
              onClick={() => {
                if (!wasCorrect && !evaluate) {
                  onSelect(quiz, opt.id);
                }
              }}
              disabled={wasCorrect || evaluate}
              aria-pressed={isSel}
            >
              <span className="quiz-dot" aria-hidden="true">
                {marker ? <span className={`quiz-marker ${stateClass}`}>{marker}</span> : null}
                {!marker && stateClass ? <span className={`inner-dot ${stateClass}`} /> : null}
              </span>
              <span className="quiz-option-text">{opt.optionText}</span>
            </button>
          );
        })}
      </div>

      <div className="practice-quiz-footer">
        {wasCorrect
          ? "Richtig beantwortet"
          : evaluate
          ? "Schau dir die markierten Antworten an."
          : "Wähle die passende Antwort aus."}
      </div>
    </div>
  );

  return (
    <>
      {renderQuizBlock(false)}
      <PracticeFullscreenModal
        open={isExpanded}
        title={richHtmlToPlainText(quiz.question) || `Quizfrage ${index + 1}`}
        subtitle={`Quizfrage ${index + 1}`}
        onClose={() => setIsExpanded(false)}
      >
        {renderQuizBlock(true)}
      </PracticeFullscreenModal>
    </>
  );
}

const toOptionIds = (value) =>
  Array.isArray(value) ? value.map(String).filter(Boolean) : [];

export function AltklausurQuizPreview({
  question,
  index,
  isExpandedView = false,
  onCheckAnswer,
  checking = false,
}) {
  const options = useMemo(
    () => (question?.options || []).slice().sort(sortByPositionThenId),
    [question]
  );
  const isMultiple = isMultipleQuizType(question?.questionType);
  const [selectedIds, setSelectedIds] = useState([]);
  const [evaluate, setEvaluate] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [serverCheckResult, setServerCheckResult] = useState(null);
  const [checkError, setCheckError] = useState("");
  const correctIds = useMemo(
    () => options.filter(isCorrectOption).map((opt) => opt.id),
    [options]
  );
  const hasSelection = selectedIds.length > 0;
  const hasLocalAnswerKey = correctIds.length > 0;
  const serverIsCorrect =
    evaluate && serverCheckResult ? Boolean(serverCheckResult.isCorrect) : false;
  const isCorrect =
    evaluate &&
    (hasLocalAnswerKey
      ? selectedIds.length === correctIds.length &&
        selectedIds.every((id) => correctIds.includes(id))
      : serverIsCorrect);
  const canCheck = hasSelection && (hasLocalAnswerKey || typeof onCheckAnswer === "function");

  useEffect(() => {
    setSelectedIds([]);
    setEvaluate(false);
    setServerCheckResult(null);
    setCheckError("");
  }, [question?.id]);

  const selectOption = (optionId) => {
    if (evaluate) return;
    setSelectedIds((prev) => {
      if (isMultiple) {
        return prev.includes(optionId)
          ? prev.filter((id) => id !== optionId)
          : [...prev, optionId];
      }
      return [optionId];
    });
  };

  const handleCheckAnswer = async () => {
    if (checking) return;

    if (evaluate) {
      setEvaluate(false);
      setSelectedIds([]);
      setServerCheckResult(null);
      setCheckError("");
      return;
    }

    if (!canCheck) return;

    if (hasLocalAnswerKey || typeof onCheckAnswer !== "function") {
      setEvaluate(true);
      return;
    }

    setCheckError("");
    try {
      const result = await onCheckAnswer(question, selectedIds);
      setServerCheckResult({
        isCorrect: Boolean(result?.isCorrect),
        selectedOptionIds: toOptionIds(result?.selectedOptionIds).length
          ? toOptionIds(result?.selectedOptionIds)
          : selectedIds,
        pointsEarned: Number(result?.pointsEarned || 0),
      });
      setEvaluate(true);
    } catch (error) {
      setCheckError(
        error?.message ||
          "Antwort konnte nicht geprüft werden. Bitte versuch es erneut."
      );
    }
  };

  const preview = (
    <div
      className={`quiz-block practice-quiz-block alt-klausur-quiz-preview ${
        isExpandedView ? "practice-expanded-card" : ""
      } ${isCorrect ? "correct-block" : evaluate ? "incorrect-block" : ""}`}
    >
      <div className="practice-quiz-header">
        <div className="practice-quiz-titlewrap">
          <span className="practice-quiz-kicker">Prüfungsfrage {index + 1}</span>
          <div
            className="practice-quiz-question lh-lessonContent"
            dangerouslySetInnerHTML={{
              __html: normalizeRichHtmlForDisplay(question.questionText, "Prüfungsfrage"),
            }}
          />
        </div>
        <div className="practice-card-actions">
          <div className="practice-quiz-badges">
            <span className="practice-quiz-mode">
              {isMultiple ? "Mehrfachauswahl" : "Single Choice"}
            </span>
            <span className="practice-quiz-required">{question.points || 1} Punkte</span>
          </div>
          {!isExpandedView ? (
            <PracticeExpandButton
              onClick={() => setIsExpanded(true)}
              label={`Prüfungsfrage ${index + 1} in Großansicht öffnen`}
            />
          ) : null}
        </div>
      </div>

      <div className="practice-quiz-options">
        {options.map((opt) => {
          const isSelected = selectedIds.includes(opt.id);
          let stateClass = "";

          if (evaluate) {
            if (hasLocalAnswerKey) {
              if (isCorrectOption(opt)) {
                stateClass = "correct";
              } else if (isSelected) {
                stateClass = "incorrect";
              }
            } else if (isSelected) {
              stateClass = serverIsCorrect ? "correct" : "incorrect";
            }
          } else if (isSelected) {
            stateClass = "selected";
          }

          const marker =
            stateClass === "correct" ? "✓" : stateClass === "incorrect" ? "×" : "";

          return (
            <button
              type="button"
              key={opt.id}
              className={`quiz-option ${stateClass}`}
              onClick={() => selectOption(opt.id)}
              disabled={evaluate}
              aria-pressed={isSelected}
            >
              <span className="quiz-dot" aria-hidden="true">
                {marker ? <span className={`quiz-marker ${stateClass}`}>{marker}</span> : null}
                {!marker && stateClass ? <span className={`inner-dot ${stateClass}`} /> : null}
              </span>
              <span className="quiz-option-text">{opt.optionText}</span>
            </button>
          );
        })}
      </div>

      <div
        className={`practice-quiz-footer alt-klausur-quiz-footer ${
          isCorrect ? "is-correct" : evaluate ? "is-incorrect" : ""
        }`}
      >
        <div>
          {isCorrect
            ? "Richtig beantwortet."
            : evaluate
            ? "Noch nicht ganz richtig. Schau dir die markierten Antworten an."
            : question.hint
            ? `Hinweis: ${question.hint}`
            : "Wähle die passende Antwort aus."}
        </div>
        {evaluate && question.explanation ? (
          <div className="alt-klausur-explanation">Erklärung: {question.explanation}</div>
        ) : null}
        {checkError ? <div className="alt-klausur-explanation">{checkError}</div> : null}
        <button
          type="button"
          className="check-button practice-check-button alt-klausur-check-button"
          disabled={!canCheck || checking}
          onClick={handleCheckAnswer}
        >
          {checking ? "Wird geprüft..." : evaluate ? "Erneut versuchen" : "Antwort prüfen"}
        </button>
      </div>
    </div>
  );

  if (isExpandedView) {
    return preview;
  }

  return (
    <>
      {preview}
      <PracticeFullscreenModal
        open={isExpanded}
        title={richHtmlToPlainText(question.questionText) || `Prüfungsfrage ${index + 1}`}
        subtitle={`Prüfungsfrage ${index + 1}`}
        onClose={() => setIsExpanded(false)}
      >
        <AltklausurQuizPreview
          question={question}
          index={index}
          isExpandedView
          onCheckAnswer={onCheckAnswer}
          checking={checking}
        />
      </PracticeFullscreenModal>
    </>
  );
}

export function StudentGenerateButton({
  children,
  disabled = false,
  locked = false,
  lockTitle,
  loading = false,
  onClick,
  title,
}) {
  const effectiveTitle = locked ? lockTitle || title : title;

  return (
    <span
      className={`student-generate-wrap ${locked ? "is-locked" : ""}`}
      data-lock-title={effectiveTitle || ""}
    >
      <button
        type="button"
        className={`student-generate-btn ${locked ? "is-locked" : ""}`}
        disabled={disabled || loading}
        onClick={onClick}
        title={effectiveTitle}
      >
        {loading ? (
          <span className="student-generate-loading">
            Wird generiert
            <span className="ai-waiting-dots" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
          </span>
        ) : (
          <>
            {locked ? <span className="student-generate-lock" aria-hidden="true" /> : null}
            <span>{children}</span>
          </>
        )}
      </button>
    </span>
  );
}

export function GeneratedQuizPreview({ quiz, index }) {
  const options = useMemo(
    () => (quiz?.options || []).slice().sort(sortByPositionThenId),
    [quiz]
  );
  const isMultiple = isMultipleQuizType(quiz?.questionType || quiz?.question_type);

  return (
    <div className="quiz-block practice-quiz-block generated-preview-card">
      <div className="practice-quiz-header">
        <div className="practice-quiz-titlewrap">
          <span className="practice-quiz-kicker">KI-Vorschau {index + 1}</span>
          <div
            className="practice-quiz-question lh-lessonContent"
            dangerouslySetInnerHTML={{
              __html: normalizeRichHtmlForDisplay(
                quiz.question || quiz.questionText || quiz.question_text,
                "Quizfrage"
              ),
            }}
          />
        </div>
        <div className="practice-card-actions">
          <div className="practice-quiz-badges">
            <span className="practice-quiz-mode">
              {isMultiple ? "Mehrfachauswahl" : "Single Choice"}
            </span>
            <span className="practice-quiz-required">Temporär</span>
          </div>
        </div>
      </div>

      <div className="practice-quiz-options">
        {options.map((opt, optionIndex) => (
          <button
            type="button"
            key={opt.id || `${quiz.id || index}-${optionIndex}`}
            className="quiz-option"
            disabled
            aria-disabled="true"
          >
            <span className="quiz-dot" aria-hidden="true" />
            <span className="quiz-option-text">
              {opt.optionText || opt.option_text || opt.text || String(opt)}
            </span>
          </button>
        ))}
      </div>

      <div className="practice-quiz-footer">
        Temporäre KI-Vorschau. Diese Frage wurde nicht im Kurs gespeichert.
      </div>
    </div>
  );
}

export function AltklausurQuizCarousel({
  questions,
  activeIndex,
  onActiveIndexChange,
  compactSidePreviews = false,
  versionId = "",
}) {
  const safeQuestions = questions || [];
  const activeQuestion = safeQuestions[activeIndex] || safeQuestions[0];
  const [quizSession, setQuizSession] = useState(null);
  const [checkingQuestionId, setCheckingQuestionId] = useState(null);
  const [startAltklausurQuizSession] = useMutation(
    START_ALTKLAUSUR_QUIZ_SESSION_MUTATION
  );
  const [saveAltklausurQuizSessionAnswer] = useMutation(
    SAVE_ALTKLAUSUR_QUIZ_SESSION_ANSWER_MUTATION
  );

  useEffect(() => {
    setQuizSession(null);
    setCheckingQuestionId(null);
  }, [versionId]);

  const ensureQuizSession = useCallback(async () => {
    if (!versionId) {
      throw new Error("Klausur-Sitzung konnte nicht gestartet werden.");
    }

    if (
      quizSession?.id &&
      String(quizSession.altklausurVersionID || "") === String(versionId) &&
      quizSession.status === "in_progress"
    ) {
      return quizSession;
    }

    const { data } = await startAltklausurQuizSession({
      variables: {
        input: {
          altklausurVersionID: versionId,
          mode: "guided",
        },
      },
    });
    const session = data?.startAltklausurQuizSession;
    if (!session?.id) {
      throw new Error("Klausur-Sitzung konnte nicht gestartet werden.");
    }
    setQuizSession(session);
    return session;
  }, [quizSession, startAltklausurQuizSession, versionId]);

  const checkAnswerWithSession = useCallback(
    async (question, selectedOptionIds) => {
      const questionId = question?.id;
      if (!questionId) {
        throw new Error("Quizfrage konnte nicht geprüft werden.");
      }

      setCheckingQuestionId(questionId);
      try {
        const session = await ensureQuizSession();
        const sessionQuestion = (session.sessionQuestions || []).find(
          (item) => String(item?.questionID || "") === String(questionId)
        );
        if (!sessionQuestion?.id) {
          throw new Error("Quizfrage wurde in der Klausur-Sitzung nicht gefunden.");
        }

        const { data } = await saveAltklausurQuizSessionAnswer({
          variables: {
            input: {
              sessionQuestionID: sessionQuestion.id,
              optionIDs: selectedOptionIds,
            },
          },
        });
        const updated = data?.saveAltklausurQuizSessionAnswer;
        if (!updated?.id) {
          throw new Error("Antwort konnte nicht geprüft werden.");
        }

        setQuizSession((prev) => {
          const base = prev?.id ? prev : session;
          return {
            ...base,
            sessionQuestions: (base.sessionQuestions || []).map((item) =>
              item.id === updated.id ? { ...item, ...updated } : item
            ),
          };
        });

        return {
          isCorrect: Boolean(updated.isCorrect),
          selectedOptionIds: updated.selectedOptionIds || selectedOptionIds,
          pointsEarned: updated.pointsEarned || 0,
        };
      } finally {
        setCheckingQuestionId(null);
      }
    },
    [ensureQuizSession, saveAltklausurQuizSessionAnswer]
  );

  if (!activeQuestion) {
    return (
      <div className="practice-empty-state">
        <div className="practice-empty-title">Keine Quizfragen veröffentlicht</div>
        <div className="practice-empty-copy">
          Für diese Altklausur sind noch keine Quizfragen sichtbar.
        </div>
      </div>
    );
  }

  const currentIndex = Math.max(0, safeQuestions.findIndex((question) => question.id === activeQuestion.id));
  const previousQuestion =
    safeQuestions.length > 1
      ? safeQuestions[(currentIndex - 1 + safeQuestions.length) % safeQuestions.length]
      : null;
  const nextQuestion =
    safeQuestions.length > 1 ? safeQuestions[(currentIndex + 1) % safeQuestions.length] : null;
  const hasPreviews = safeQuestions.length > 1;
  const progressPercent = Math.round(((currentIndex + 1) / safeQuestions.length) * 100);

  const goTo = (nextIndex) => {
    if (safeQuestions.length === 0) return;
    const bounded = (nextIndex + safeQuestions.length) % safeQuestions.length;
    onActiveIndexChange?.(bounded);
  };

  return (
    <div
      className={`lesson-task-carousel alt-quiz-carousel ${
        compactSidePreviews ? "is-compact-side-preview" : ""
      }`}
    >
      <div className="lesson-task-progress">
        <div>
          <span>Fortschritt</span>
          <strong>{currentIndex + 1} von {safeQuestions.length} Quizfragen</strong>
        </div>
        <div className="lesson-task-progressbar" aria-hidden="true">
          <span style={{ width: `${progressPercent}%` }} />
        </div>
      </div>

      <div className={`lesson-task-stage ${hasPreviews ? "" : "is-single-task"}`}>
        {previousQuestion ? (
          <button
            type="button"
            className="lesson-task-preview lesson-task-preview-prev"
            onClick={() => goTo(currentIndex - 1)}
            aria-label={`Quizfrage ${
              currentIndex === 0 ? safeQuestions.length : currentIndex
            } öffnen`}
          >
            {compactSidePreviews ? (
              <strong className="lesson-task-preview-number">
                {currentIndex === 0 ? safeQuestions.length : currentIndex}
              </strong>
            ) : (
              <>
                <span>Quizfrage {currentIndex === 0 ? safeQuestions.length : currentIndex}</span>
                <strong>{richHtmlToPlainText(previousQuestion.questionText) || "Quizfrage"}</strong>
              </>
            )}
          </button>
        ) : null}

        <div className="lesson-task-active">
          <AltklausurQuizPreview
            question={activeQuestion}
            index={currentIndex}
            onCheckAnswer={versionId ? checkAnswerWithSession : undefined}
            checking={checkingQuestionId === activeQuestion.id}
          />
          <div className="lesson-task-controls">
            <button
              type="button"
              className="lesson-task-arrow"
              onClick={() => goTo(currentIndex - 1)}
              disabled={safeQuestions.length <= 1}
              aria-label="Vorherige Quizfrage"
            >
              ‹
            </button>

            {safeQuestions.length > 1 ? (
              <div className="lesson-task-dots" aria-label="Quizfragen auswählen">
                {safeQuestions.map((question, index) => (
                  <button
                    key={question.id}
                    type="button"
                    className={index === currentIndex ? "active" : ""}
                    onClick={() => onActiveIndexChange?.(index)}
                    aria-label={`Quizfrage ${index + 1}`}
                  />
                ))}
              </div>
            ) : null}

            <button
              type="button"
              className="lesson-task-arrow"
              onClick={() => goTo(currentIndex + 1)}
              disabled={safeQuestions.length <= 1}
              aria-label="Nächste Quizfrage"
            >
              ›
            </button>
          </div>
        </div>

        {nextQuestion ? (
          <button
            type="button"
            className="lesson-task-preview lesson-task-preview-next"
            onClick={() => goTo(currentIndex + 1)}
            aria-label={`Quizfrage ${
              currentIndex + 2 > safeQuestions.length ? 1 : currentIndex + 2
            } öffnen`}
          >
            {compactSidePreviews ? (
              <strong className="lesson-task-preview-number">
                {currentIndex + 2 > safeQuestions.length ? 1 : currentIndex + 2}
              </strong>
            ) : (
              <>
                <span>Quizfrage {currentIndex + 2 > safeQuestions.length ? 1 : currentIndex + 2}</span>
                <strong>{richHtmlToPlainText(nextQuestion.questionText) || "Quizfrage"}</strong>
              </>
            )}
          </button>
        ) : null}
      </div>
    </div>
  );
}

function GeneratedLessonTaskCard({ task, index }) {
  return (
    <article className="lesson-task-generated-card">
      <div className="lesson-task-generated-head">
        <span>KI-generierte Aufgabe {index + 1}</span>
        <h4>{task.title || "Generierte Aufgabe"}</h4>
      </div>

      <div
        className="lesson-task-body lh-lessonContent lh-lessonContent-normalized"
        dangerouslySetInnerHTML={{ __html: task.taskText || "" }}
      />

      <div className="lesson-task-support">
        {task.hintText ? (
          <details className="lesson-task-details">
            <summary>Hinweis anzeigen</summary>
            <div className="lh-lessonContent" dangerouslySetInnerHTML={{ __html: task.hintText }} />
          </details>
        ) : null}

        {task.answerText ? (
          <details className="lesson-task-details">
            <summary>Musterlösung anzeigen</summary>
            <div className="lh-lessonContent" dangerouslySetInnerHTML={{ __html: task.answerText }} />
          </details>
        ) : null}
      </div>
    </article>
  );
}

export function LessonTaskCard({
  task,
  index,
  isExpandedView = false,
  generatedTasks = [],
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const nestedGeneratedTasks = generatedTasks;

  const card = (
    <article className={`lesson-task-card ${isExpandedView ? "practice-expanded-card" : ""}`}>
      <div className="lesson-task-card-head">
        <div>
          <span className="lesson-task-kicker">
            {isExpandedView ? `Aufgabe ${index + 1}` : "Aktuelle Aufgabe"}
          </span>
          <h3>{task.title || "Aufgabe"}</h3>
        </div>
        <div className="practice-card-actions">
          <div className="lesson-task-meta">
            {task.difficulty ? <span>{task.difficulty}</span> : null}
            {Number(task.estimatedMinutes) > 0 ? (
              <span>{task.estimatedMinutes} min</span>
            ) : null}
          </div>
          {!isExpandedView ? (
            <PracticeExpandButton
              onClick={() => setIsExpanded(true)}
              label={`Aufgabe ${index + 1} in Großansicht öffnen`}
            />
          ) : null}
        </div>
      </div>

      <div
        className="lesson-task-body lh-lessonContent lh-lessonContent-normalized"
        dangerouslySetInnerHTML={{ __html: task.taskText || "" }}
      />

      <div className="lesson-task-support">
        {task.hintText ? (
          <details className="lesson-task-details">
            <summary>Hinweis anzeigen</summary>
            <div className="lh-lessonContent" dangerouslySetInnerHTML={{ __html: task.hintText }} />
          </details>
        ) : null}

        {task.answerText ? (
          <details className="lesson-task-details">
            <summary>Musterlösung anzeigen</summary>
            <div className="lh-lessonContent" dangerouslySetInnerHTML={{ __html: task.answerText }} />
          </details>
        ) : null}
      </div>

      {nestedGeneratedTasks.length > 0 ? (
        <section className="lesson-task-generated-section">
          <div className="lesson-task-generated-title">
            <span />
            <strong>KI-generierte Aufgaben zu dieser Aufgabe</strong>
            <span />
          </div>
          <div className="lesson-task-generated-list">
            {nestedGeneratedTasks.map((generatedTask, generatedIndex) => (
              <GeneratedLessonTaskCard
                key={generatedTask.id || `${task.id || "task"}-generated-${generatedIndex}`}
                task={generatedTask}
                index={generatedIndex}
              />
            ))}
          </div>
        </section>
      ) : null}
    </article>
  );

  if (isExpandedView) {
    return card;
  }

  return (
    <>
      {card}
      <PracticeFullscreenModal
        open={isExpanded}
        title={task.title || "Aufgabe"}
        subtitle={`Aufgabe ${index + 1}`}
        onClose={() => setIsExpanded(false)}
      >
        <LessonTaskCard
          task={task}
          index={index}
          isExpandedView
          generatedTasks={generatedTasks}
        />
      </PracticeFullscreenModal>
    </>
  );
}

export function LessonTaskCarousel({
  tasks,
  activeIndex,
  onActiveIndexChange,
  groupGeneratedTasks = false,
  showSidePreviews = false,
}) {
  const safeTasks = tasks || [];
  const taskGroups = groupGeneratedTasks
    ? buildLessonTaskGroups(safeTasks)
    : safeTasks.map((task) => ({ task, generatedTasks: [] }));
  const requestedTask = safeTasks[activeIndex] || safeTasks[0] || null;
  const requestedTaskId = taskId(requestedTask);
  const requestedOriginId =
    requestedTask && isGeneratedLessonTask(requestedTask) ? getOriginTaskId(requestedTask) : "";
  let currentIndex = 0;

  if (requestedOriginId) {
    const originIndex = taskGroups.findIndex((group) => taskId(group.task) === requestedOriginId);
    if (originIndex >= 0) currentIndex = originIndex;
  } else if (requestedTaskId) {
    const requestedIndex = taskGroups.findIndex((group) => taskId(group.task) === requestedTaskId);
    if (requestedIndex >= 0) currentIndex = requestedIndex;
  }

  const activeGroup = taskGroups[currentIndex] || taskGroups[0];
  const activeTask = activeGroup?.task;

  if (!activeTask) {
    return (
      <div className="practice-empty-state">
        <div className="practice-empty-title">Aufgaben demnächst verfügbar</div>
        <div className="practice-empty-copy">
          Für diese Lektion folgen in Kürze passende Aufgaben.
        </div>
      </div>
    );
  }

  const previousGroup =
    taskGroups.length > 1 ? taskGroups[(currentIndex - 1 + taskGroups.length) % taskGroups.length] : null;
  const nextGroup = taskGroups.length > 1 ? taskGroups[(currentIndex + 1) % taskGroups.length] : null;
  const hasSidePreviews = showSidePreviews && taskGroups.length > 1;
  const goTo = (nextIndex) => {
    if (taskGroups.length === 0) return;
    const bounded = (nextIndex + taskGroups.length) % taskGroups.length;
    const nextTask = taskGroups[bounded]?.task;
    const rawIndex = safeTasks.findIndex((task) => taskId(task) === taskId(nextTask));
    onActiveIndexChange?.(rawIndex >= 0 ? rawIndex : bounded);
  };

  const carouselControls = (
    <div className="lesson-task-controls lesson-task-controls-top">
      <button
        type="button"
        className="lesson-task-arrow"
        onClick={() => goTo(currentIndex - 1)}
        disabled={taskGroups.length <= 1}
        aria-label="Vorherige Aufgabe"
      >
        ‹
      </button>

      {taskGroups.length > 1 ? (
        <div className="lesson-task-dots" aria-label="Aufgaben auswählen">
          {taskGroups.map((group, index) => (
            <button
              key={group.task.id || `task-dot-${index}`}
              type="button"
              className={index === currentIndex ? "active" : ""}
              onClick={() => goTo(index)}
              aria-label={`Aufgabe ${index + 1}`}
            />
          ))}
        </div>
      ) : null}

      <button
        type="button"
        className="lesson-task-arrow"
        onClick={() => goTo(currentIndex + 1)}
        disabled={taskGroups.length <= 1}
        aria-label="Nächste Aufgabe"
      >
        ›
      </button>
    </div>
  );

  return (
    <div className="lesson-task-carousel">
      <div className="lesson-task-carousel-top">
        <div className="lesson-task-counter">
          <span>Aufgaben</span>
          <strong>{currentIndex + 1} von {taskGroups.length}</strong>
        </div>
        {carouselControls}
      </div>

      <div className={`lesson-task-stage ${hasSidePreviews ? "" : "is-single-task"}`}>
        {hasSidePreviews && previousGroup?.task ? (
          <button
            type="button"
            className="lesson-task-preview lesson-task-preview-prev"
            onClick={() => goTo(currentIndex - 1)}
          >
            <span>Aufgabe {currentIndex === 0 ? taskGroups.length : currentIndex}</span>
            <strong>{previousGroup.task.title || "Aufgabe"}</strong>
          </button>
        ) : null}

        <div className="lesson-task-active">
          <LessonTaskCard
            task={activeTask}
            index={currentIndex}
            generatedTasks={activeGroup?.generatedTasks || []}
          />
        </div>

        {hasSidePreviews && nextGroup?.task ? (
          <button
            type="button"
            className="lesson-task-preview lesson-task-preview-next"
            onClick={() => goTo(currentIndex + 1)}
          >
            <span>Aufgabe {currentIndex + 2 > taskGroups.length ? 1 : currentIndex + 2}</span>
            <strong>{nextGroup.task.title || "Aufgabe"}</strong>
          </button>
        ) : null}
      </div>
    </div>
  );
}
